import {
  ArrowRight,
  Braces,
  Columns3,
  Download,
  FolderDown,
  Ruler,
  Scaling,
  Sheet,
  Table2,
  Check,
  Code2,
  FileSpreadsheet,
  FolderUp,
  Layers,
  MousePointer2,
  Palette,
  Type,
  Workflow,
  Zap,
  Images,
  QrCode,
  Wand2,
  History,
  MessageSquare,
  ScrollText,
  Film,
  MousePointerClick,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  applyModifications,
  createEmptyDoc,
  createQrLayer,
  createShapeLayer,
  createTextLayer,
  PLANS,
  type TemplateDoc,
} from '@bannermatic/core';
import { Scene } from '@bannermatic/scene';
import { Logo } from '../components/Logo';
import { EditorShowcase } from './landing/EditorShowcase';
import { BrandShowcase } from './landing/BrandShowcase';
import { MotionShowcase } from './landing/MotionShowcase';
import { CodeBlock } from '../components/ui';
import { cn, formatNumber, videoMinutes } from '../lib/format';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { usePricing } from '../lib/pricing';
import { useAiEnabled } from '../lib/features';
import { t as translateNow, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import { useMarketingCopy } from '../marketing/content';
import { HUB_PATHS, PAGE_PATHS } from '../marketing/content/shared';
import { MarketingFooter } from '../marketing/MarketingLayout';
import { useLocalePath, usePageLocale } from '../marketing/PublicRoute';

function demoDoc(): TemplateDoc {
  const doc = createEmptyDoc(1080, 1080);
  doc.background = {
    type: 'linear',
    angle: 135,
    stops: [
      { offset: 0, color: '#1E1B4B' },
      { offset: 1, color: '#4338CA' },
    ],
  };
  doc.layers.push(
    createShapeLayer({
      name: 'card',
      x: 560,
      y: 120,
      width: 440,
      height: 600,
      borderRadius: 40,
      fill: {
        type: 'linear',
        angle: 160,
        stops: [
          { offset: 0, color: '#F472B6' },
          { offset: 1, color: '#FB923C' },
        ],
      },
    }),
    createTextLayer({
      name: 'emoji',
      x: 560,
      y: 260,
      width: 440,
      height: 320,
      text: '👟',
      fontSize: 220,
      align: 'center',
      verticalAlign: 'middle',
      fontFamily: 'Inter',
    }),
    createTextLayer({
      name: 'title',
      x: 70,
      y: 120,
      width: 450,
      height: 360,
      text: translateNow('landing.demo1Title'),
      fontFamily: 'Montserrat',
      fontWeight: 800,
      fontSize: 92,
      lineHeight: 1.04,
      color: '#FFFFFF',
      autoFit: true,
      minFontSize: 40,
    }),
    createTextLayer({
      name: 'subtitle',
      x: 70,
      y: 500,
      width: 450,
      height: 150,
      text: translateNow('landing.demo1Subtitle'),
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: 36,
      lineHeight: 1.3,
      color: '#C7D2FE',
      maxLines: 3,
    }),
    createShapeLayer({
      name: 'badge_bg',
      x: 70,
      y: 780,
      width: 300,
      height: 110,
      borderRadius: 55,
      fill: { type: 'solid', color: '#FACC15' },
    }),
    createTextLayer({
      name: 'badge',
      x: 70,
      y: 780,
      width: 300,
      height: 110,
      text: '-30%',
      fontFamily: 'Unbounded',
      fontWeight: 800,
      fontSize: 54,
      color: '#111827',
      align: 'center',
      verticalAlign: 'middle',
    }),
    createTextLayer({
      name: 'price',
      x: 560,
      y: 780,
      width: 440,
      height: 110,
      text: '$29.90',
      fontFamily: 'Golos Text',
      fontWeight: 700,
      fontSize: 76,
      color: '#FFFFFF',
      align: 'right',
      verticalAlign: 'middle',
    }),
    createQrLayer({
      name: 'qr',
      x: 70,
      y: 930,
      width: 100,
      height: 100,
      background: '#FFFFFF',
      value: 'https://bannermatic.app',
    }),
    createTextLayer({
      name: 'cta',
      x: 190,
      y: 945,
      width: 800,
      height: 70,
      text: 'bannermatic.app',
      fontFamily: 'Inter',
      fontWeight: 600,
      fontSize: 34,
      color: '#E0E7FF',
      verticalAlign: 'middle',
    }),
  );
  return doc;
}

const ROWS = [
  { n: 1, badge: '-30%', price: '$29.90', emoji: '👟', fill: '#F472B6' },
  { n: 2, badge: 'NEW', price: '$74.90', emoji: '🏃', fill: '#34D399' },
  { n: 3, badge: '-50%', price: '$14.95', emoji: '🛍️', fill: '#F59E0B' },
  { n: 4, badge: 'HIT', price: '$10', from: true, emoji: '🎁', fill: '#60A5FA' },
] as const;

function useBuiltinFonts() {
  useEffect(() => {
    if (document.getElementById('bm-builtin-fonts')) return;
    const link = document.createElement('link');
    link.id = 'bm-builtin-fonts';
    link.rel = 'stylesheet';
    link.href = '/fonts/builtin.css';
    document.head.appendChild(link);
  }, []);
}

function LiveDemo() {
  const t = useT();
  const base = useMemo(demoDoc, []);
  const [active, setActive] = useState(0);
  useBuiltinFonts();
  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % ROWS.length), 2600);
    return () => clearInterval(id);
  }, []);
  const rows = ROWS.map((r) => ({
    ...r,
    title: t(`landing.demo${r.n}Title`),
    subtitle: t(`landing.demo${r.n}Subtitle`),
    price: 'from' in r ? t('landing.demoFrom', { price: r.price }) : r.price,
  }));
  const row = rows[active];
  const doc = useMemo(
    () =>
      applyModifications(base, {
        title: row.title,
        subtitle: row.subtitle,
        badge: row.badge,
        price: row.price,
        emoji: row.emoji,
        card: { fill: row.fill },
      }).doc,
    [base, row.title, row.subtitle, row.badge, row.price, row.emoji, row.fill],
  );
  const [size, setSize] = useState(400);
  const frame = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className="relative flex min-w-0 flex-col-reverse items-center gap-5 xl:flex-row xl:items-center">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur xl:w-72 xl:shrink-0">
        <div className="flex items-center gap-2 border-b border-white/10 px-4 py-2.5 text-xs text-indigo-200">
          <FileSpreadsheet className="size-4" /> products.csv ·{' '}
          {t('landing.demoRows', { n: ROWS.length })}
        </div>
        <table className="w-full text-left text-[13px]">
          <thead className="text-indigo-300">
            <tr>
              <th className="px-4 py-2 font-mono font-medium">title</th>
              <th className="px-2 py-2 font-mono font-medium">price</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={i}
                className={cn(
                  'cursor-pointer transition',
                  i === active
                    ? 'bg-indigo-500/30 text-white'
                    : 'text-indigo-100/70 hover:bg-white/5',
                )}
                onClick={() => setActive(i)}
              >
                {/* Full titles (wrapping, never cut) */}
                <td className="px-4 py-2 leading-snug">{r.title}</td>
                <td className="py-2 pr-4 pl-2 align-top whitespace-nowrap">{r.price}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="relative aspect-square w-full max-w-[400px] shrink-0">
        {/* Mascot perched on the top edge of the live preview */}
        <div className="absolute -top-11 sm:-top-13 right-4 sm:right-6 z-20 select-none transition-transform duration-300 hover:scale-110 hover:-rotate-3 cursor-pointer">
          <img
            src="/mascot.png"
            alt="Bannermatic Mascot"
            className="h-16 sm:h-20 w-auto object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
          />
        </div>

        <div
          ref={frame}
          className="relative size-full overflow-hidden rounded-2xl shadow-2xl shadow-indigo-950/60 ring-1 ring-white/10"
        >
          <div
            className="absolute top-0 left-0"
            style={{
              transform: `scale(${size / 1080})`,
              transformOrigin: '0 0',
              width: 1080,
              height: 1080,
            }}
          >
            <Scene doc={doc} />
          </div>
        </div>
      </div>
    </div>
  );
}

/** The use-case strip under the hero links to the pages about each use case. */
const USE_CASE_STRIP = [
  ['landing.uc1', 'product-cards'],
  ['landing.uc2', 'ad-creatives'],
  ['landing.uc3', 'social-media'],
  ['landing.uc4', 'og-images'],
  ['landing.uc5', 'certificates'],
] as const;

const SHOWCASE_POINTS = [
  {
    icon: Columns3,
    title: 'showcase.p1Title',
    text: 'showcase.p1Text',
    tone: { card: 'bg-violet-50/70 ring-violet-100', icon: 'bg-violet-100 text-violet-700' },
  },
  {
    icon: Scaling,
    title: 'showcase.p2Title',
    text: 'showcase.p2Text',
    tone: { card: 'bg-sky-50/70 ring-sky-100', icon: 'bg-sky-100 text-sky-700' },
  },
  {
    icon: Ruler,
    title: 'showcase.p3Title',
    text: 'showcase.p3Text',
    tone: { card: 'bg-rose-50/70 ring-rose-100', icon: 'bg-rose-100 text-rose-700' },
  },
  {
    icon: Braces,
    title: 'showcase.p4Title',
    text: 'showcase.p4Text',
    tone: { card: 'bg-amber-50/70 ring-amber-100', icon: 'bg-amber-100 text-amber-700' },
  },
] as const;

const FEATURES = [
  { icon: MousePointer2, title: 'landing.f1Title', text: 'landing.f1Text', tone: 'violet' },
  { icon: Code2, title: 'landing.f2Title', text: 'landing.f2Text', tone: 'sky' },
  { icon: Workflow, title: 'landing.f3Title', text: 'landing.f3Text', tone: 'emerald' },
  { icon: Type, title: 'landing.f4Title', text: 'landing.f4Text', tone: 'amber' },
  { icon: FolderUp, title: 'landing.f5Title', text: 'landing.f5Text', tone: 'rose' },
  { icon: Zap, title: 'landing.f6Title', text: 'landing.f6Text', tone: 'indigo' },
  { icon: Images, title: 'landing.f7Title', text: 'landing.f7Text', tone: 'sky' },
  { icon: QrCode, title: 'landing.f8Title', text: 'landing.f8Text', tone: 'violet' },
  { icon: Wand2, title: 'landing.f9Title', text: 'landing.f9Text', tone: 'emerald' },
] as const;

/** Accent per card: icon chip, soft gradient and hover ring. */
const TONES = {
  violet: {
    icon: 'bg-violet-100 text-violet-700',
    card: 'from-violet-50 hover:ring-violet-200',
  },
  sky: { icon: 'bg-sky-100 text-sky-700', card: 'from-sky-50 hover:ring-sky-200' },
  emerald: {
    icon: 'bg-emerald-100 text-emerald-700',
    card: 'from-emerald-50 hover:ring-emerald-200',
  },
  amber: { icon: 'bg-amber-100 text-amber-700', card: 'from-amber-50 hover:ring-amber-200' },
  rose: { icon: 'bg-rose-100 text-rose-700', card: 'from-rose-50 hover:ring-rose-200' },
  indigo: {
    icon: 'bg-indigo-100 text-indigo-700',
    card: 'from-indigo-50 hover:ring-indigo-200',
  },
} as const;

/** Plan accents on the pricing cards. */
const PLAN_TONE: Record<string, { bar: string; check: string; price: string }> = {
  free: { bar: 'from-zinc-300 to-zinc-400', check: 'text-zinc-500', price: 'text-zinc-900' },
  starter: { bar: 'from-sky-400 to-cyan-400', check: 'text-sky-600', price: 'text-sky-700' },
  pro: {
    bar: 'from-violet-500 to-fuchsia-500',
    check: 'text-violet-600',
    price: 'text-violet-700',
  },
  business: {
    bar: 'from-amber-400 to-orange-500',
    check: 'text-amber-600',
    price: 'text-amber-700',
  },
};

export function LandingPage() {
  const t = useT();
  const aiEnabled = useAiEnabled();
  const user = useSession((s) => s.user);
  const cta = user ? '/app/templates' : '/signup';
  const pricing = usePricing();
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const lp = useLocalePath();
  const useCasesLabel = useMarketingCopy(usePageLocale()).ui.navUseCases;
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');

  useEffect(() => {
    const onScroll = () => {
      setScrolled((window.scrollY || document.documentElement.scrollTop) > 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const sectionIds = ['features', 'api', 'spreadsheets', 'pricing'];
    const elements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));

    if (!elements.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -65% 0px' },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const scrollToAnchor = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const id = href.slice(1);
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.history.pushState(null, '', href);
      }
    }
  };

  const scrollToTop = (e: React.MouseEvent) => {
    if (window.location.pathname === lp('/')) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.pushState(null, '', lp('/'));
    }
  };

  return (
    <div className="min-h-full overflow-x-hidden bg-white">
      {/* Fixed Sticky Header */}
      <header
        className={cn(
          'fixed top-0 inset-x-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-[#0f0d2e]/85 backdrop-blur-md border-b border-white/10 shadow-lg shadow-black/25 py-3 sm:py-3.5'
            : 'bg-transparent border-b border-transparent py-5 sm:py-6',
        )}
      >
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-4 sm:gap-6 sm:px-6">
          <div
            onClick={scrollToTop}
            className="shrink-0 cursor-pointer transition-opacity hover:opacity-90"
          >
            <Logo light compact to={lp('/')} />
          </div>
          <nav className="hidden items-center gap-6 text-sm md:flex">
            <a
              href="#features"
              onClick={(e) => scrollToAnchor(e, '#features')}
              className={cn(
                'transition-colors duration-150',
                activeSection === 'features'
                  ? 'text-white font-semibold'
                  : 'text-indigo-100/80 hover:text-white',
              )}
            >
              {t('landing.navFeatures')}
            </a>
            <Link
              to={lp(HUB_PATHS['use-cases'])}
              className="text-indigo-100/80 transition-colors duration-150 hover:text-white"
            >
              {useCasesLabel}
            </Link>
            <a
              href="#api"
              onClick={(e) => scrollToAnchor(e, '#api')}
              className={cn(
                'transition-colors duration-150',
                activeSection === 'api'
                  ? 'text-white font-semibold'
                  : 'text-indigo-100/80 hover:text-white',
              )}
            >
              API
            </a>
            <a
              href="#spreadsheets"
              onClick={(e) => scrollToAnchor(e, '#spreadsheets')}
              className={cn(
                'transition-colors duration-150',
                activeSection === 'spreadsheets'
                  ? 'text-white font-semibold'
                  : 'text-indigo-100/80 hover:text-white',
              )}
            >
              {t('landing.navSpreadsheets')}
            </a>
            <a
              href="#pricing"
              onClick={(e) => scrollToAnchor(e, '#pricing')}
              className={cn(
                'transition-colors duration-150',
                activeSection === 'pricing'
                  ? 'text-white font-semibold'
                  : 'text-indigo-100/80 hover:text-white',
              )}
            >
              {t('landing.navPricing')}
            </a>
            <a
              href="/docs"
              className="text-indigo-100/80 transition-colors duration-150 hover:text-white"
            >
              {t('landing.navDocs')}
            </a>
          </nav>
          <div className="flex-1" />
          <LanguageSwitcher tone="dark" compact className="shrink-0 px-2 sm:px-2.5" />
          {user ? (
            <Link
              to="/app/templates"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-white px-3 py-2 whitespace-nowrap sm:px-4 text-sm font-semibold text-indigo-950 shadow-sm transition hover:bg-indigo-50 hover:scale-[1.02] active:scale-[0.98]"
            >
              {t('landing.openApp')} <ArrowRight className="size-4" />
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden text-sm font-medium text-indigo-100 transition hover:text-white sm:block"
              >
                {t('landing.login')}
              </Link>
              <Link
                to="/signup"
                className="shrink-0 rounded-lg bg-white px-3 py-2 whitespace-nowrap sm:px-4 text-sm font-semibold text-indigo-950 shadow-sm transition hover:bg-indigo-50 hover:scale-[1.02] active:scale-[0.98]"
              >
                {t('landing.startFree')}
              </Link>
            </>
          )}
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden bg-[#0f0d2e] text-white pt-20 sm:pt-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(99,102,241,0.45),transparent),radial-gradient(40%_40%_at_10%_80%,rgba(236,72,153,0.25),transparent)]" />
        <section className="relative mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)] items-center gap-12 px-6 pt-6 pb-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          <div>
            <div className="mb-5 inline-flex rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-indigo-100">
              {t('landing.badge')}
            </div>
            <h1 className="text-4xl leading-[1.08] font-extrabold tracking-tight md:text-6xl">
              {t('landing.title')}
            </h1>
            <p className="mt-6 max-w-xl text-lg text-indigo-100/80">{t('landing.subtitle')}</p>
            {/* Phones: two full-width buttons of the same size, one under the other */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to={cta}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-3 font-semibold text-indigo-950 shadow-lg hover:bg-indigo-50 sm:w-auto"
              >
                {t('landing.ctaPrimary')} <ArrowRight className="size-4" />
              </Link>
              <a
                href="#api"
                onClick={(e) => scrollToAnchor(e, '#api')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/20 px-6 py-3 font-semibold text-white hover:bg-white/10 sm:w-auto"
              >
                <Code2 className="size-4" /> {t('landing.ctaSecondary')}
              </a>
            </div>
            <p className="mt-4 text-sm text-indigo-200/70">{t('landing.noCard')}</p>
          </div>
          <LiveDemo />
        </section>
      </div>

      {/* Use cases */}
      <section className="border-b border-zinc-100 bg-zinc-50">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-6 py-6 text-sm font-medium text-zinc-500">
          {USE_CASE_STRIP.map(([k, id]) => (
            <Link key={k} to={lp(PAGE_PATHS[id])} className="hover:text-brand-700">
              {t(k)}
            </Link>
          ))}
        </div>
      </section>

      {/* Editor */}
      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_40%_at_15%_10%,rgba(167,139,250,0.18),transparent),radial-gradient(40%_40%_at_90%_30%,rgba(56,189,248,0.15),transparent),radial-gradient(40%_40%_at_50%_100%,rgba(244,114,182,0.12),transparent)]" />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
              <MousePointer2 className="size-3.5" /> {t('showcase.badge')}
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
              {t('showcase.title')}
            </h2>
            <p className="mt-4 text-lg text-zinc-600">{t('showcase.subtitle')}</p>
          </div>
          <div className="mt-12">
            <EditorShowcase />
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SHOWCASE_POINTS.map(({ icon: Icon, title, text, tone }) => (
              <div key={title} className={cn('rounded-2xl p-5 ring-1', tone.card)}>
                <div className={cn('mb-3 inline-flex rounded-xl p-2', tone.icon)}>
                  <Icon className="size-5" />
                </div>
                <h3 className="font-semibold text-zinc-900">{t(title)}</h3>
                <p className="mt-1 text-sm leading-relaxed text-zinc-600">{t(text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Motion: a static banner animated in one click */}
      <section id="motion" className="relative scroll-mt-24 overflow-hidden bg-zinc-50 py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(40%_45%_at_90%_20%,rgba(217,70,239,0.12),transparent),radial-gradient(40%_45%_at_5%_90%,rgba(139,92,246,0.12),transparent)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-fuchsia-100 px-3 py-1 text-xs font-semibold text-fuchsia-700">
              <Film className="size-3.5" /> {t('motionShowcase.badge')}
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
              {t('motionShowcase.title')}
            </h2>
            <p className="mt-4 text-lg text-zinc-600">{t('motionShowcase.subtitle')}</p>
            <ul className="mt-8 space-y-5">
              {(
                [
                  [MousePointerClick, 'motionShowcase.p1Title', 'motionShowcase.p1Text'],
                  [SlidersHorizontal, 'motionShowcase.p2Title', 'motionShowcase.p2Text'],
                  [Zap, 'motionShowcase.p3Title', 'motionShowcase.p3Text'],
                ] as const
              ).map(([Icon, title, text]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-fuchsia-100 text-fuchsia-700">
                    <Icon className="size-5" />
                  </span>
                  <span>
                    <span className="block font-semibold text-zinc-900">{t(title)}</span>
                    <span className="mt-0.5 block text-sm leading-relaxed text-zinc-600">
                      {t(text)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <MotionShowcase />
            <p className="mt-4 text-center text-xs text-zinc-500">{t('motionShowcase.hint')}</p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="scroll-mt-24 mx-auto max-w-7xl px-6 py-24">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900 md:text-4xl">
            {t('landing.featuresTitle')}
          </h2>
          <p className="mt-4 text-lg text-zinc-500">{t('landing.featuresSubtitle')}</p>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, text, tone }) => (
            <div
              key={title}
              className={cn(
                'rounded-2xl bg-gradient-to-br to-white to-60% p-6 ring-1 ring-zinc-200 transition hover:-translate-y-0.5 hover:shadow-lg',
                TONES[tone].card,
              )}
            >
              <div className={cn('mb-4 inline-flex rounded-xl p-2.5', TONES[tone].icon)}>
                <Icon className="size-5" />
              </div>
              <h3 className="text-lg font-semibold text-zinc-900">{t(title)}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">{t(text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Brands & teams: one template set for every client */}
      <section
        id="brands"
        className="relative scroll-mt-24 overflow-hidden bg-zinc-950 py-24 text-white"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_50%_at_10%_20%,rgba(99,102,241,0.28),transparent),radial-gradient(40%_45%_at_95%_80%,rgba(168,85,247,0.22),transparent)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
          <div>
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              {t('landing.brandsTitle')}
            </h2>
            <p className="mt-4 text-lg text-zinc-300">{t('landing.brandsSubtitle')}</p>
            <ul className="mt-8 space-y-5">
              {(
                [
                  [Palette, 'landing.brandsP1Title', 'landing.brandsP1Text'],
                  [History, 'landing.brandsP2Title', 'landing.brandsP2Text'],
                  [MessageSquare, 'landing.brandsP3Title', 'landing.brandsP3Text'],
                  [ScrollText, 'landing.brandsP4Title', 'landing.brandsP4Text'],
                ] as const
              ).map(([Icon, title, text]) => (
                <li key={title} className="flex gap-4">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-indigo-200 ring-1 ring-white/15">
                    <Icon className="size-5" />
                  </span>
                  <span>
                    <span className="block font-semibold text-white">{t(title)}</span>
                    <span className="mt-0.5 block text-sm leading-relaxed text-zinc-400">
                      {t(text)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-white/[0.04] p-4 ring-1 ring-white/10 backdrop-blur sm:p-6">
            <div className="mb-4 flex items-center justify-between gap-3 text-xs">
              <span className="font-semibold text-zinc-200">{t('landing.brandsDemoLabel')}</span>
              <code className="truncate rounded bg-white/10 px-2 py-1 font-mono text-[11px] text-zinc-300">
                brand_id: brd_…
              </code>
            </div>
            <BrandShowcase />
            <p className="mt-4 text-center text-xs text-zinc-400">{t('landing.brandsDemoHint')}</p>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-gradient-to-b from-indigo-50/60 via-violet-50/40 to-white py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="text-center text-3xl font-bold tracking-tight text-zinc-900">
            {t('landing.howTitle')}
          </h2>
          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {[
              {
                icon: Palette,
                title: 'landing.step1Title',
                text: 'landing.step1Text',
                tone: 'from-violet-500 to-fuchsia-500',
                ink: 'text-violet-600',
              },
              {
                icon: Layers,
                title: 'landing.step2Title',
                text: 'landing.step2Text',
                tone: 'from-sky-500 to-cyan-400',
                ink: 'text-sky-600',
              },
              {
                icon: Zap,
                title: 'landing.step3Title',
                text: 'landing.step3Text',
                tone: 'from-amber-400 to-orange-500',
                ink: 'text-amber-600',
              },
            ].map(({ icon: Icon, title, text, tone, ink }, i) => (
              <div
                key={title}
                className="relative rounded-2xl bg-white p-7 shadow-sm ring-1 ring-zinc-200"
              >
                <div
                  className={cn(
                    'absolute -top-4 left-7 flex size-8 items-center justify-center rounded-full bg-gradient-to-br text-sm font-bold text-white shadow-md',
                    tone,
                  )}
                >
                  {i + 1}
                </div>
                <Icon className={cn('mb-4 size-6', ink)} />
                <h3 className="text-lg font-semibold text-zinc-900">
                  {t(title as 'landing.step1Title')}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                  {t(text as 'landing.step1Text')}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API */}
      <section
        id="api"
        className="scroll-mt-24 mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 lg:grid-cols-2"
      >
        <div>
          <h2 className="text-3xl font-bold tracking-tight text-zinc-900">
            {t('landing.apiTitle')}
          </h2>
          <p className="mt-4 text-lg text-zinc-600">{t('landing.apiText')}</p>
          <ul className="mt-6 space-y-3 text-zinc-700">
            {(['landing.api1', 'landing.api2', 'landing.api3', 'landing.api4'] as const).map(
              (k) => (
                <li key={k} className="flex gap-3">
                  <Check className="mt-0.5 size-5 shrink-0 text-emerald-600" />
                  {t(k)}
                </li>
              ),
            )}
          </ul>
          <div className="mt-8">
            <a
              href="/docs"
              className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition"
            >
              {t('landing.navDocs')} <ArrowRight className="size-4" />
            </a>
          </div>
        </div>
        <div className="space-y-3">
          <CodeBlock
            language="request"
            code={`curl -X POST https://api.bannermatic.app/v1/renders?wait=true \\
  -H "Authorization: Bearer bm_live_…" \\
  -d '{
    "template_id": "tpl_promo",
    "format": "png",
    "layers": {
      "title": "Black Friday",
      "price": { "text": "$14.95", "color": "#FACC15" },
      "photo": "https://cdn.shop.com/sneakers.jpg",
      "badge": null
    }
  }'`}
          />
          <CodeBlock
            language="response"
            code={`{
  "id": "rnd_8fK2…",
  "status": "done",
  "url": "https://cdn.bannermatic.app/…/rnd_8fK2.png",
  "width": 1080, "height": 1080
}`}
          />
        </div>
      </section>

      {/* Spreadsheets & No-Code Studio */}
      <section
        id="spreadsheets"
        className="scroll-mt-24 relative overflow-hidden border-y border-zinc-100 bg-gradient-to-b from-slate-50/70 via-emerald-50/20 to-white py-24"
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(45%_35%_at_80%_30%,rgba(16,185,129,0.08),transparent),radial-gradient(40%_40%_at_20%_70%,rgba(99,102,241,0.06),transparent)]" />
        <div className="relative mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,1.35fr)] xl:gap-14">
            <div className="min-w-0 max-w-xl">
              <h2 className="text-3xl font-extrabold tracking-tight text-zinc-900 sm:text-4xl lg:text-4xl xl:text-5xl leading-[1.15]">
                {t('landing.tablesTitle')}
              </h2>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-zinc-600">
                {t('landing.tablesSubtitle')}
              </p>

              <div className="mt-8 space-y-5">
                <div className="flex gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 shadow-sm">
                    <Sheet className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900">
                      {t('landing.tablesPoint1Title')}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                      {t('landing.tablesPoint1Text')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 shadow-sm">
                    <Table2 className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900">
                      {t('landing.tablesPoint2Title')}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                      {t('landing.tablesPoint2Text')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-700 shadow-sm">
                    <FolderDown className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900">
                      {t('landing.tablesPoint3Title')}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-zinc-600">
                      {t('landing.tablesPoint3Text')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to={cta}
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/25 transition hover:bg-emerald-700 hover:shadow-xl"
                >
                  {t('landing.tablesCta')} <ArrowRight className="size-4" />
                </Link>
                <span className="text-xs font-medium text-zinc-500">{t('landing.noCard')}</span>
              </div>
            </div>

            {/* Right side: Mockup Window + Mascot pointing */}
            <div className="min-w-0 relative flex flex-col items-center sm:flex-row sm:items-end justify-center lg:justify-end gap-2 sm:gap-3 xl:gap-5">
              <div className="w-full max-w-[350px] sm:max-w-[380px] xl:max-w-[420px] shrink min-w-0 overflow-hidden rounded-2xl border border-zinc-200/80 bg-white shadow-xl shadow-zinc-900/10">
                {/* Window title bar */}
                <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50/90 px-3.5 py-2.5 sm:px-4 sm:py-3">
                  <span className="size-2.5 rounded-full bg-rose-400" />
                  <span className="size-2.5 rounded-full bg-amber-400" />
                  <span className="size-2.5 rounded-full bg-emerald-400" />
                  <span className="ml-2 inline-flex items-center gap-1.5 font-mono text-xs font-medium text-zinc-600 truncate">
                    <Sheet className="size-3.5 text-emerald-600 shrink-0" />
                    autumn_sale_catalog.csv
                  </span>
                </div>

                {/* Column mapping bar */}
                <div className="border-b border-zinc-100 bg-zinc-50/40 p-2.5 sm:p-3 text-xs">
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                    {t('landing.tablesPoint2Title')}
                  </div>
                  <div className="flex flex-wrap gap-1 font-mono text-[10px]">
                    <div className="inline-flex items-center gap-1 rounded-md border border-violet-200/80 bg-violet-50 px-1.5 py-0.5 text-violet-800">
                      <span className="text-zinc-400">title</span>
                      <span>➔</span>
                      <span className="font-semibold">{t('landing.tablesMockColProduct')}</span>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-md border border-emerald-200/80 bg-emerald-50 px-1.5 py-0.5 text-emerald-800">
                      <span className="text-zinc-400">price</span>
                      <span>➔</span>
                      <span className="font-semibold">{t('landing.tablesMockColPrice')}</span>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-md border border-rose-200/80 bg-rose-50 px-1.5 py-0.5 text-rose-800">
                      <span className="text-zinc-400">badge</span>
                      <span>➔</span>
                      <span className="font-semibold">{t('landing.tablesMockColDiscount')}</span>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-md border border-sky-200/80 bg-sky-50 px-1.5 py-0.5 text-sky-800">
                      <span className="text-zinc-400">photo</span>
                      <span>➔</span>
                      <span className="font-semibold">{t('landing.tablesMockColPhoto')}</span>
                    </div>
                  </div>
                </div>

                {/* Table rows - cleanly fits without any horizontal scroll */}
                <div className="overflow-hidden">
                  <table className="w-full text-left text-[11px] sm:text-xs">
                    <thead>
                      <tr className="border-b border-zinc-100 font-medium text-zinc-400">
                        <th className="w-6 px-1.5 py-2 font-normal text-center">#</th>
                        <th className="px-2 py-2">{t('landing.tablesMockColProduct')}</th>
                        <th className="w-14 px-1.5 py-2 text-right sm:text-left">
                          {t('landing.tablesMockColPrice')}
                        </th>
                        <th className="w-14 px-1.5 py-2 text-center">
                          {t('landing.tablesMockColDiscount')}
                        </th>
                        <th className="w-16 px-2 py-2 text-right">
                          {t('landing.tablesMockColStatus')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-50 font-medium text-zinc-700">
                      <tr className="hover:bg-zinc-50/60 transition">
                        <td className="px-1.5 py-2 text-center font-mono text-zinc-400">1</td>
                        <td className="px-2 py-2 text-zinc-900 truncate max-w-[110px] sm:max-w-[140px]">
                          👟 Nike Air Zoom 40
                        </td>
                        <td className="px-1.5 py-2 font-semibold text-emerald-700 text-right sm:text-left whitespace-nowrap">
                          $79.90
                        </td>
                        <td className="px-1.5 py-2 text-center">
                          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                            -30%
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                            <Check className="size-3" /> {t('landing.tablesMockDone')}
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-zinc-50/60 transition">
                        <td className="px-1.5 py-2 text-center font-mono text-zinc-400">2</td>
                        <td className="px-2 py-2 text-zinc-900 truncate max-w-[110px] sm:max-w-[140px]">
                          🧥 Tech Fleece Hoodie
                        </td>
                        <td className="px-1.5 py-2 font-semibold text-emerald-700 text-right sm:text-left whitespace-nowrap">
                          $94.00
                        </td>
                        <td className="px-1.5 py-2 text-center">
                          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                            -20%
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                            <Check className="size-3" /> {t('landing.tablesMockDone')}
                          </span>
                        </td>
                      </tr>
                      <tr className="hover:bg-zinc-50/60 transition">
                        <td className="px-1.5 py-2 text-center font-mono text-zinc-400">3</td>
                        <td className="px-2 py-2 text-zinc-900 truncate max-w-[110px] sm:max-w-[140px]">
                          🎒 Commuter Pack 24L
                        </td>
                        <td className="px-1.5 py-2 font-semibold text-emerald-700 text-right sm:text-left whitespace-nowrap">
                          $45.50
                        </td>
                        <td className="px-1.5 py-2 text-center">
                          <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] font-bold text-rose-800">
                            -15%
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                            <Check className="size-3" /> {t('landing.tablesMockDone')}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Bottom bar with progress and export button */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 bg-zinc-50/90 p-2.5 sm:p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-16 overflow-hidden rounded-full bg-zinc-200">
                      <div className="h-full w-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400" />
                    </div>
                    <span className="text-[11px] font-semibold text-emerald-700">
                      450 / 450 (100%)
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-zinc-800 transition">
                    <Download className="size-3" />
                    <span>ZIP (28 MB)</span>
                  </div>
                </div>
              </div>

              {/* Mascot pointing directly at the table */}
              <div className="relative shrink-0 flex items-end -ml-2 sm:-ml-3 lg:-ml-4 xl:-ml-2 z-10">
                <img
                  src="/mascot-finger.png"
                  alt="Bannermatic Mascot"
                  className="h-36 sm:h-44 lg:h-48 xl:h-60 2xl:h-72 w-auto object-contain drop-shadow-xl select-none transition duration-300 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section
        id="pricing"
        className="scroll-mt-24 relative bg-gradient-to-b from-white via-violet-50/50 to-sky-50/40 py-24"
      >
        <div className="mx-auto max-w-7xl px-6">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900">
              {t('landing.pricingTitle')}
            </h2>
            <p className="mt-4 text-lg text-zinc-500">{t('landing.pricingSubtitle')}</p>
            <div className="mt-6 flex items-center justify-center">
              <div className="inline-flex rounded-lg bg-white p-0.5 text-sm ring-1 ring-zinc-200">
                {(['month', 'year'] as const).map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setInterval(i)}
                    className={cn(
                      'rounded-md px-3 py-1 font-medium transition',
                      interval === i
                        ? 'bg-zinc-900 text-white'
                        : 'text-zinc-600 hover:text-zinc-900',
                    )}
                  >
                    {i === 'month'
                      ? t('billing.monthly')
                      : t('billing.yearly', { n: pricing.yearlyDiscount() })}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {Object.values(PLANS).map((p) => (
              <div
                key={p.id}
                className={cn(
                  'relative flex flex-col overflow-hidden rounded-2xl bg-white p-6 pt-7 ring-1 ring-zinc-200 transition hover:-translate-y-0.5 hover:shadow-lg',
                  p.highlighted && 'shadow-xl ring-2 shadow-violet-500/15 ring-violet-500',
                )}
              >
                <div
                  className={cn(
                    'absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r',
                    PLAN_TONE[p.id].bar,
                  )}
                />
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-zinc-900">{p.name}</h3>
                  {p.highlighted && (
                    <span className="rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                      {t('billing.popular')}
                    </span>
                  )}
                </div>
                <div className={cn('mt-3 text-4xl font-bold', PLAN_TONE[p.id].price)}>
                  {pricing.format(pricing.monthly(p.id, interval).amount)}
                  <span className="text-base font-normal text-zinc-500">/{t('billing.mo')}</span>
                </div>
                {interval === 'year' && p.id !== 'free' && (
                  <div className="mt-1 text-xs text-zinc-500">
                    {t('billing.billedYearly', {
                      amount: pricing.format(pricing.price(p.id, 'year').amount),
                    })}
                  </div>
                )}
                <ul className="mt-5 flex-1 space-y-2 text-sm text-zinc-600">
                  <li className="flex gap-2">
                    <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                    {t('billing.fRenders', { n: formatNumber(p.limits.rendersPerMonth) })}
                  </li>
                  <li className="flex gap-2">
                    <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                    {t('billing.fVideo', videoMinutes(p.limits.videoSecondsPerMonth))}
                  </li>
                  {aiEnabled && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fAi', { n: formatNumber(p.limits.aiCredits) })}
                    </li>
                  )}
                  <li className="flex gap-2">
                    <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                    {p.limits.templates === null
                      ? t('billing.fTemplatesUnlimited')
                      : t('billing.fTemplates', { n: p.limits.templates })}
                  </li>
                  {p.limits.customSizes !== 0 && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {p.limits.customSizes === null
                        ? t('billing.fSizesUnlimited')
                        : t('billing.fSizes', { n: p.limits.customSizes })}
                    </li>
                  )}
                  <li className="flex gap-2">
                    <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                    {t('billing.fBatch', { n: formatNumber(p.limits.maxBatchSize) })}
                  </li>
                  {p.features.linkedSizes && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fLinked')}
                    </li>
                  )}
                  {p.features.qrStyles && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fQrStyles')}
                    </li>
                  )}
                  {(p.limits.brands ?? 1) > 0 && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fBrands')}
                      {p.limits.brands === 1 ? ' (1)' : ''}
                    </li>
                  )}
                  {p.features.versionHistory && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fHistory')}
                    </li>
                  )}
                  {p.features.customFonts && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fFonts')}
                    </li>
                  )}
                  {p.features.integrations && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fIntegrations')}
                    </li>
                  )}
                  {p.features.scheduling && (
                    <li className="flex gap-2">
                      <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                      {t('billing.fSchedule')}
                    </li>
                  )}
                  <li className="flex gap-2">
                    <Check className={cn('size-4 shrink-0', PLAN_TONE[p.id].check)} />
                    {t('billing.fPreviews')}
                  </li>
                </ul>
                <Link
                  to={cta}
                  className={cn(
                    'mt-6 rounded-xl py-2.5 text-center text-sm font-semibold',
                    p.highlighted
                      ? 'bg-brand-600 text-white hover:bg-brand-700'
                      : 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200',
                  )}
                >
                  {p.id === 'free'
                    ? t('landing.startFree')
                    : t('landing.tryPlan', { plan: p.name })}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-24 mx-auto max-w-3xl px-6 py-24">
        <h2 className="text-center text-3xl font-bold tracking-tight text-zinc-900">
          {t('landing.faqTitle')}
        </h2>
        <div className="mt-10 divide-y divide-zinc-200">
          {([1, 2, 3, 4, 5, 6, 7, 8] as const).map((n) => (
            <details key={n} className="group py-5">
              <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-zinc-900">
                {t(`landing.q${n}`)}
                <span className="text-zinc-400 transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-zinc-600">{t(`landing.a${n}`)}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50/90 via-white to-violet-50/70 p-8 shadow-xl shadow-indigo-100/40 sm:p-12">
          <div className="relative z-10 flex flex-col items-center justify-between gap-8 md:flex-row md:text-left">
            <div className="max-w-xl text-center md:text-left">
              <h2 className="text-3xl font-extrabold tracking-tight text-zinc-900 sm:text-4xl">
                {t('landing.finalTitle')}
              </h2>
              <p className="mt-3 text-base leading-relaxed text-zinc-600 sm:text-lg">
                {t('landing.finalText')}
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4 md:justify-start">
                <Link
                  to={cta}
                  className="inline-flex items-center gap-2 rounded-xl bg-brand-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-brand-500/25 transition hover:bg-brand-700 hover:shadow-xl"
                >
                  {t('landing.ctaPrimary')} <ArrowRight className="size-4" />
                </Link>
                <span className="text-xs font-medium text-zinc-500">{t('landing.noCard')}</span>
              </div>
            </div>
            <div className="relative shrink-0">
              <div className="absolute -inset-4 -z-10 rounded-full bg-indigo-200/40 blur-2xl" />
              <img
                src="/mascot-salute.png"
                alt="Bannermatic Mascot"
                className="h-52 w-auto object-contain drop-shadow-md select-none transition duration-300 hover:scale-105 sm:h-60 lg:h-64"
              />
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
